/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { LoadingOverlay, TextInput, Center, Text, Button } from '@mantine/core';
import { i18n } from '@lingui/core';
import { closeAllModals, openModal } from '@mantine/modals';
import { IconTemplate } from '@tabler/icons';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ValidationS } from '../../../Service/ValidationS';
import { QuestionnaireTemplateS } from '../../../Service/QuestionnaireTemplateS';
import { QuestionnaireTemplateDto } from '../../../Types/QuestionnaireT';

interface CreateTemplateProps {
  existingNames?: string[];
}

// eslint-disable-next-line import/prefer-default-export
export const openCreateQuestionnaireTemplateModal = (props: CreateTemplateProps) => {
  openModal({
    title: (
      <Text weight="bolder" size="xl" align="center">
        Vorlage erstellen
      </Text>
    ),
    children: <CreateTemplateModal {...props} />,
  });
};

const CreateTemplateModal: FC<CreateTemplateProps> = ({ existingNames = [] }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const nameTaken = existingNames.includes(name);
  const validName = ValidationS.validateTemplateName(name);

  const createTemplate = async () => {
    setLoading(true);
    const resJSON = await QuestionnaireTemplateS.createTemplate(name);
    if (resJSON) {
      closeAllModals();
      navigate(`/settings/template/questionnaire/${name}`);
    }
  };

  return (
    <>
      <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ size: 'xl' }} />
      <TextInput
        mt="md"
        label="Name der Vorlage"
        icon={<IconTemplate size={20} />}
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        error={
          // eslint-disable-next-line no-nested-ternary
          !validName
            ? 'Der Name enthält unerlaubte Sonderzeichen: %/?#:&+"'
            : nameTaken
            ? 'Dieser Name ist bereits vergeben.'
            : null
        }
      />
      <Center mt={40}>
        <Button disabled={nameTaken || name === '' || loading} onClick={createTemplate} sx={{ minWidth: 200 }}>
          {i18n._('common.apply')}
        </Button>
      </Center>
    </>
  );
};

interface EditTemplateProps {
  template: QuestionnaireTemplateDto;
  editHandler: (patch: Partial<QuestionnaireTemplateDto>) => Promise<boolean>;
}

// eslint-disable-next-line import/prefer-default-export
export const openEditQuestionnaireTemplateModal = (props: EditTemplateProps) => {
  openModal({
    title: (
      <Text weight="bolder" size="xl" align="center">
        Vorlage erstellen
      </Text>
    ),
    children: <EditQuestionnaireTemplateModal {...props} />,
  });
};

const EditQuestionnaireTemplateModal: FC<EditTemplateProps> = ({ template, editHandler }) => {
  const { name: initialName } = template;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(initialName);
  const [existingNames, setExistingNames] = useState<string[]>([]);
  const nameTaken = existingNames.includes(name);
  const validName = ValidationS.validateTemplateName(name);

  useEffect(() => {
    setLoading(true);
    QuestionnaireTemplateS.fetchTemplatesNames()
      .then((names) => {
        setExistingNames(names.filter((n) => n !== initialName));
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editTemplate = async () => {
    setLoading(true);
    const edited = await editHandler({ name });
    setLoading(false);
    if (edited) {
      closeAllModals();
      navigate(`/settings/template/questionnaire/${name}`);
    }
  };

  return (
    <>
      <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ size: 'xl' }} />
      <TextInput
        mt="md"
        label="Name der Vorlage"
        icon={<IconTemplate size={20} />}
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        error={
          // eslint-disable-next-line no-nested-ternary
          !validName
            ? 'Der Name enthält unerlaubte Sonderzeichen: %/?#:&+"'
            : nameTaken
            ? 'Dieser Name ist bereits vergeben.'
            : null
        }
      />
      <Center mt={40}>
        <Button
          disabled={nameTaken || name === '' || name === initialName}
          onClick={editTemplate}
          sx={{ minWidth: 200 }}
        >
          {i18n._('common.apply')}
        </Button>
      </Center>
    </>
  );
};
